import type { FC, ReactNode } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState
} from 'react'

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'halation-theme'

type ThemeValue = {
    theme: Theme
    toggle: () => void
}

const ThemeContext = createContext<ThemeValue>({
    theme: 'light',
    toggle: () => undefined
})

export const useTheme = () => useContext(ThemeContext)

export const ThemeProvider: FC<{ children: ReactNode }> = ({ children }): ReactNode => {
    const [theme, setTheme] = useState<Theme>('light')

    useEffect(() => {
        const saved = window.localStorage.getItem(STORAGE_KEY)

        if (saved === 'dark' || saved === 'light') setTheme(saved)
        else if (document.documentElement.dataset.theme === 'dark') setTheme('dark')
    }, [])

    useEffect(() => {
        document.documentElement.dataset.theme = theme
    }, [theme])

    const toggle = useCallback(() => {
        setTheme(current => {
            const next: Theme = current === 'dark' ? 'light' : 'dark'

            window.localStorage.setItem(STORAGE_KEY, next)

            return next
        })
    }, [])

    return (
        <ThemeContext.Provider value={{ theme, toggle }}>
            {children}
        </ThemeContext.Provider>
    )
}
